import React from 'react';

// Valutaenhet för visning: SEK (och saknad valuta) visas som "kr", övriga som koden.
export function currencyLabel(currency) {
  return !currency || currency === 'SEK' ? 'kr' : currency;
}

export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

// Kundens videospelare laddas först när kunden klickar (DPIA A5, beslut Erik 2026-11-01):
// YouTube/Vimeo får kundens IP-adress och sätter lagring så fort spelaren laddas.
function playerUrl(url) {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { src: `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?autoplay=1&rel=0`, provider: 'YouTube' };
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return { src: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&dnt=1`, provider: 'Vimeo' };
  return null;
}

export function VideoEmbed({ url }) {
  const [active, setActive] = React.useState(false);
  const player = url ? playerUrl(url) : null;
  if (!player) return null;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginTop: '12px', borderRadius: '10px', overflow: 'hidden', background: '#0F172A' }}>
      {active ? (
        <iframe
          src={player.src}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Övningsvideo"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: 'transparent', color: '#FFFFFF', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', fontFamily: 'inherit' }}
        >
          <span style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          </span>
          <span style={{ fontSize: '0.92em', fontWeight: 600 }}>Spela video</span>
          <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.7)', maxWidth: '80%', textAlign: 'center' }}>Videon spelas upp via {player.provider}</span>
        </button>
      )}
    </div>
  );
}

export const DAY_NAMES_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
export const DAY_NAMES = ['Söndag', 'Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag'];

export function generateSlots(schedule, duration, breakDuration) {
  if (!schedule) return [];
  const slots = [];
  const [startH, startM] = schedule.start_time.split(':').map(Number);
  const [endH, endM] = schedule.end_time.split(':').map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;
  const step = duration + breakDuration;

  let current = startMin;
  while (current + duration <= endMin) {
    const slotStart = `${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`;
    const slotEnd = `${String(Math.floor((current + duration) / 60)).padStart(2, '0')}:${String((current + duration) % 60).padStart(2, '0')}`;
    slots.push({ start: slotStart, end: slotEnd });
    current += step;
  }
  return slots;
}

export function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function isCampaignActive(service, date = new Date().toISOString().slice(0, 10)) {
  if (!service?.campaign_enabled || service?.campaign_price == null) return false;
  const started = !service.campaign_start_date || service.campaign_start_date <= date;
  const notEnded = !service.campaign_end_date || service.campaign_end_date >= date;
  return started && notEnded;
}

export function getDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getCurrentWeek(cp) {
  if (!cp.start_date) return 1;
  const start = new Date(cp.start_date);
  const now = new Date();
  const diffDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.floor(diffDays / 7) + 1);
}

export function getTotalWeeks(cp) {
  if (!cp.start_date || !cp.end_date) return null;
  const start = new Date(cp.start_date);
  const end = new Date(cp.end_date);
  return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24) / 7));
}
